import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "./DRGReport.css";

import ReportTable from "../ReportTable";
import DRGReportFilter from "./DRGReportFilter";
import { useReport } from "../hooks/useReport";
import { batchUpdateAppliedFilters } from "../redux/ReportsSlice";

import totalOutstanding from "../../../../common/assets/images/notification-box.png";
import highPriority from "../../../../common/assets/images/highPriority.png";
import calendar from "../../../../common/assets/images/calendar.png";
import hourglass from "../../../../common/assets/images/hourglass.png";
// import { exportReportData } from "../redux/ReportsSlice";
import { getReportConfig } from "../optionsConfig";
import { useSelector } from "react-redux";

const REPORT_PATH = "/drg-outlier-report";
const reportAPIConfig = getReportConfig(REPORT_PATH);
/* ──────────────────────────────────────────────────────
 * flattenDRGEpisode
 * The API returns DRG details nested inside a `drgs[]` array.
 * ReportTable expects flat fields (drg, drgdescription, nwau, etc.)
 * We pick the DRG with is_default=true, falling back to index 0.
 ────────────────────────────────────────────────────── */
const flattenDRGEpisode = (ep) => {
  const defaultDrg = ep.drgs?.find((d) => d.is_default) ?? ep.drgs?.[0] ?? {};
  return {
    ...ep,
    drg: defaultDrg.drg_code,
    drgdescription: defaultDrg.drg_description,
    nwau: defaultDrg.weight,
    dollarValue: defaultDrg.revenue,
    drgavlos: defaultDrg.drg_alos,
    lowBound: defaultDrg.lower_bound,
    UpBound: defaultDrg.upper_bound,
  };
};

const DRGReport = () => {
  const dispatch = useDispatch();
  const appliedFilters = useSelector(
  (s) => s.reports.appliedFilters[REPORT_PATH] || {}
 );

  const {
    episodes,
    reportData,
    totalPages,
    pageNumber,
    setPageNumber,
    reportLoading,
    reportError,
    exportLoading,
    // handleExport,
    handleReset,
    resetSignal,
    setSortPayload,
     pageSize,
    setPageSize
  } = useReport(REPORT_PATH);

  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("episode_id");
  const [sortPayload, setSortPayloadState] = useState({});

  const handleColumnFilterApply = useCallback(
    (filters) => {
      dispatch(
        batchUpdateAppliedFilters({
          reportPath: REPORT_PATH,
          filters,
        }),
      );

      setPageNumber(1);
    },
    [dispatch, setPageNumber],
  );

  // DRG has dual-field search: by episode_id OR by drg code

  const handleSearchChange = useCallback(
    ({ field, value } = {}) => {
      const resolved = field || searchField;
      setSearchField(resolved);
      setSearchText(value || "");
      dispatch(
        batchUpdateAppliedFilters({
          reportPath: REPORT_PATH,
          filters: {
            search: resolved === "episode_id" ? value || "" : "",
            drg_search: resolved === "drg" ? value || "" : "",
          },
        }),
      );
      setPageNumber(1);
    },
    [dispatch, searchField, setPageNumber],
  );

  // Flatten nested drgs[] into each episode row for ReportTable columns
  const flatEpisodes = useMemo(
    () => episodes.map(flattenDRGEpisode),
    [episodes],
  );
   useEffect(() => {
  const updatePageSize = () => {
    const screenHeight = window.innerHeight;

    // header + widgets + pagination space
    const availableHeight = screenHeight - 335;

    // approximate row height
    const rowHeight = 36;

    const dynamicRows = Math.floor(availableHeight / rowHeight);

    setPageSize(dynamicRows > 12 ? dynamicRows : 12);
  };

  updatePageSize();

  window.addEventListener("resize", updatePageSize);

  return () => {
    window.removeEventListener("resize", updatePageSize);
  };
}, []);
//   const buildExportPayload = (filters, search, sort) => {
//   const payload = {};

//   if (search) payload.search = search;
//   if (filters.drg_search) payload.drg_search = filters.drg_search;

//   if (sort?.sort_by) payload.sort_by = sort.sort_by;
//   if (sort?.sort_order) payload.sort_order = sort.sort_order;

//   return payload;
// };

// const handleExport = () => {
//   const payload = buildExportPayload(appliedFilters, searchText, sortPayload);

//   dispatch(
//     exportReportData({
//       reportPath: REPORT_PATH,
//       endpoint: reportAPIConfig.exportEndpoint,
//       method: reportAPIConfig.method,
//       payload,
//     })
//   )
//     .unwrap()
//     .then((res) => {
//       const url = res?.data?.body?.download_url || res?.data?.download_url;

//       if (url) {
//         const a = document.createElement("a");
//         a.href = url;
//         a.setAttribute("download", "drg_report.csv");
//         document.body.appendChild(a);
//         a.click();
//         document.body.removeChild(a);
//       }
//     });
// };
  // ── Widget cards — response: { total_episodes, data: [...] }
  // cards_data may or may not be present depending on the endpoint version
  // const cards = reportData?.cards_data || reportData?.data?.cards_data;

  // const widgets = [
  //   {
  //     label: "Total Episodes",
  //     count: reportData?.total_episodes ?? cards?.total_episodes,
  //     bgColor: "#EFF6FF",
  //     icon: totalOutstanding,
  //   },
  //   {
  //     label: "DRG Outliers",
  //     count: cards?.drg_outliers,
  //     bgColor: "#FEF2F2",
  //     icon: highPriority,
  //   },
  //   {
  //     label: "Avg LOS",
  //     count: cards?.avg_los,
  //     bgColor: "#FEFCE8",
  //     icon: calendar,
  //   },
  //   {
  //     label: "Avg NWAU",
  //     count: cards?.avg_nwau,
  //     bgColor: "#ECFDF5",
  //     icon: hourglass,
  //   },
  // ];

  return (
    <div className="hac-report-table-container mt-3 px-2">
      <h4 className="pb-2" style={{ flexShrink: 0 }}>
        DRG Outliers Report
      </h4>

      {/* ── Widget cards ── */}
      {/* <div className="d-flex gap-2 mb-3 flex-wrap">
        {widgets.map((w) => (
          <div
            key={w.label}
            className="outstanding-widget col"
            style={{ background: w.bgColor }}>
            <div>
              <p className="mb-1">{w.label}</p>
              {reportLoading ? (
                <Skeleton width={60} />
              ) : (
                <p className="mb-0 fw-bold">{w.count ?? "0"}</p>
              )}
            </div>
            <div>
              <img src={w.icon} alt={w.label} />
            </div>
          </div>
        ))}
      </div> */}

      <ReportTable
        key="drg"
        episodes={flatEpisodes}
        isReportLoading={reportLoading}
        isReportError={reportError}
        reportPageNumber={pageNumber}
        setReportPageNumber={setPageNumber}
        // handleReportExport={handleExport}
        reportExcelLoading={exportLoading}
        handleSearchChange={handleSearchChange}
        totalPages={totalPages}
        searchText={searchText}
        setSearchText={setSearchText}
        onSortChange={setSortPayload}
        onReset={handleReset}
        resetSignal={resetSignal}
        onColumnFilterApply={handleColumnFilterApply}
      />

      <DRGReportFilter onApply={() => setPageNumber(1)} />
    </div>
  );
};

export default DRGReport;
